import { ReactNode, Fragment } from 'react';
import { ChevronRight } from 'lucide-react';
import { Term } from '../Term';
import { GLOSSARY } from './glossary';
import './FeatureList.css';

/**
 * parseText — inline-only conversion (bold / italic / code).
 *
 * Converts simple markdown-style markers in a string to React elements.
 *
 * Supported syntax:
 *   **text**   → <strong> (bold)
 *   *text*     → <em>     (italic)
 *   `text`     → <code>   (inline code / keyword highlight)
 *
 * Usage:
 *   <p>{parseText("Roll **2d12** and add your modifier.")}</p>
 */
export function parseText(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*'))
      return <em key={i}>{part.slice(1, -1)}</em>;
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={i}>{part.slice(1, -1)}</code>;
    return part;
  });
}

/* ─────────────────────────────────────────────────────────────────
   parseInline — same as parseText but also normalises em-dashes
   and supports the className="inline-strong" convention used in
   Domains.css for bold card text.
   ───────────────────────────────────────────────────────────────── */
function parseInline(text: string): ReactNode[] {
  const cleaned = text.replace(/\r/g, '').replace(/—/g, '-');
  const parts: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(cleaned)) !== null) {
    if (m.index > cursor) parts.push(cleaned.slice(cursor, m.index));
    if (m[1] !== undefined) {
      parts.push(<strong key={m.index} className="inline-strong">{m[1]}</strong>);
    } else {
      parts.push(<em key={m.index}>{m[2]}</em>);
    }
    cursor = re.lastIndex;
  }
  if (cursor < cleaned.length) parts.push(cleaned.slice(cursor));
  return parts;
}

/* ─────────────────────────────────────────────────────────────────
   wrapTermsInText — detects glossary terms in a string and wraps them
   in Term components.

   Takes a plain text string (with no markdown) and returns an array
   of React nodes with game terms wrapped in <Term>.

   Tries to match longest terms first to avoid partial matches.
   ───────────────────────────────────────────────────────────────── */
function wrapTermsInText(text: string): ReactNode[] {
  if (!text || text.trim().length === 0) return [text];

  // Build a list of all term names + aliases, sorted by length (longest first)
  const allTerms: Array<{ original: string; matchRegex: RegExp }> = [];
  for (const term of GLOSSARY) {
    allTerms.push({
      original: term.name,
      matchRegex: new RegExp(`\\b${term.name}\\b`, 'gi'),
    });
    for (const alias of term.aliases) {
      allTerms.push({
        original: alias,
        matchRegex: new RegExp(`\\b${alias}\\b`, 'gi'),
      });
    }
  }

  // Sort by length (longest first) to match longer terms before shorter ones
  allTerms.sort((a, b) => b.original.length - a.original.length);

  // Build regex that matches any term
  const pattern = allTerms.map((t) => t.original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const combinedRegex = new RegExp(`\\b(${pattern})\\b`, 'gi');

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  combinedRegex.lastIndex = 0;
  while ((match = combinedRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Add the term as a Term
    parts.push(
      <Term key={match.index}>{match[0]}</Term>
    );

    lastIndex = combinedRegex.lastIndex;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/* ─────────────────────────────────────────────────────────────────
   parseTextWithTerms — parses markdown AND wraps glossary terms.

   Usage:
     <p>{parseTextWithTerms("Roll against the enemy's **Evasion** score.")}</p>

   Steps:
     1. Parse markdown (**bold**, *italic*)
     2. For each string part, detect glossary terms and wrap in Term
     3. Return array of React nodes

   Works best for inline text. For block-level card text, use
   parseCardTextWithTerms() instead.
   ───────────────────────────────────────────────────────────────── */
export function parseTextWithTerms(text: string): ReactNode[] {
  if (!text) return [];

  // First, parse the markdown
  const markdownParsed = parseInline(text);

  // Then, for each part, detect terms and wrap them
  const result: ReactNode[] = [];
  for (let i = 0; i < markdownParsed.length; i++) {
    const part = markdownParsed[i];

    // If it's already a React element (bold, italic), keep it as-is
    if (typeof part !== 'string') {
      result.push(part);
      continue;
    }

    // If it's a string, wrap any glossary terms found in it
    const termWrapped = wrapTermsInText(part);
    result.push(...termWrapped);
  }

  return result;
}

/* ─────────────────────────────────────────────────────────────────
   parseFeatureText — smart renderer for feature body text.

   Detects two structural patterns that should not be a wall of text:

   1. Numbered list  "intro... 1. Item one. 2. Item two." → <ol>
   2. Outcome table  "intro... Critical Success: ... Success with Hope: ..."
                     → intro + <ul class="feature-outcomes">

   Falls back to parseTextWithTerms for plain text.
   ───────────────────────────────────────────────────────────────── */
const OUTCOME_LABELS = [
  'Critical Success',
  'Success with Hope',
  'Success with Fear',
  'Any Failure',
  'Failure with Hope',
  'Failure with Fear',
  'Success',
  'Failure',
];
const OUTCOME_RE = new RegExp(
  `\\b(${OUTCOME_LABELS.map((l) => l.replace(/ /g, '\\s+')).join('|')}):`,
);
const OUTCOME_SPLIT_RE = new RegExp(
  `(?=\\b(?:${OUTCOME_LABELS.map((l) => l.replace(/ /g, '\\s+')).join('|')}):)`,
);

export function parseFeatureText(text: ReactNode): ReactNode {
  if (typeof text !== 'string') return text;

  // ── 1. Numbered list ──
  if (/\b1\.\s/.test(text) && /\b2\.\s/.test(text)) {
    const listStart = text.search(/\b1\.\s/);
    const intro = text.slice(0, listStart).trimEnd();
    const listText = text.slice(listStart);
    const items = listText.split(/\s*\b\d+\.\s+/).filter((s) => s.trim().length > 0);
    return (
      <>
        {intro && <span>{parseTextWithTerms(intro)}</span>}
        <ol className="feature-list">
          {items.map((item, i) => (
            <li key={i}>{parseTextWithTerms(item.trim())}</li>
          ))}
        </ol>
      </>
    );
  }

  // ── 2. Outcome table ──
  if (OUTCOME_RE.test(text)) {
    const segments = text.split(OUTCOME_SPLIT_RE);
    const intro = segments[0].trim();
    const outcomes = segments.slice(1).map((seg) => {
      const ci = seg.indexOf(':');
      return { label: seg.slice(0, ci).trim(), body: seg.slice(ci + 1).trim() };
    });
    return (
      <>
        {intro && <><span>{parseTextWithTerms(intro)}</span>{' '}</>}
        <ul className="feature-outcomes">
          {outcomes.map((item, i) => (
            <li key={i} className="feature-outcome">
              <span className="feature-outcome__label">{item.label}:</span>{' '}
              <span>{parseTextWithTerms(item.body)}</span>
            </li>
          ))}
        </ul>
      </>
    );
  }

  // ── Default ──
  return <>{parseTextWithTerms(text)}</>;
}

/* ─────────────────────────────────────────────────────────────────
   parseCardTextWithTerms — full block-level conversion for domain card
   text, WITH glossary term linking.

   Same as parseCardText but also wraps game terms in Term components.

   Handles:
     • Empty lines               → <div className="card-text-gap" />
     • Lines starting with "- "
       or "• "                   → <div className="card-text-bullet">
     • All other non-empty lines → <p className="card-text-para">
     • Inline **bold** / *italic* inside any block
     • Game terms wrapped in Term components

   Returns a React fragment so callers can render it directly.
   ───────────────────────────────────────────────────────────────── */
export function parseCardTextWithTerms(text: string): ReactNode {
  if (!text) return null;
  const lines = text.replace(/\r/g, '').split('\n');
  const nodes: ReactNode[] = [];
  let key = 0;

  for (const raw of lines) {
    const line = raw.trim();

    if (!line) {
      nodes.push(<div key={key++} className="card-text-gap" />);
      continue;
    }

    const isBullet = line.startsWith('- ') || line.startsWith('• ');
    const content = parseTextWithTerms(isBullet ? line.slice(2) : line);

    if (isBullet) {
      nodes.push(
        <div key={key++} className="card-text-bullet">
          <ChevronRight size={12} className="card-text-bullet__arrow" />
          <span>{content}</span>
        </div>
      );
    } else {
      nodes.push(<p key={key++} className="card-text-para">{content}</p>);
    }
  }

  return <>{nodes}</>;
}

/* ─────────────────────────────────────────────────────────────────
   parseCardText — full block-level conversion for domain card text.

   Handles:
     • Empty lines               → <div className="card-text-gap" />
     • Lines starting with "- "
       or "• "                   → <div className="card-text-bullet">
     • All other non-empty lines → <p className="card-text-para">
     • Inline **bold** / *italic* inside any block

   The CSS classes (.card-text-gap, .card-text-bullet, .card-text-para,
   .inline-strong) are defined in Domains.css.

   Returns a React fragment so callers can render it directly:
     <>{card.rendered}</>
   ───────────────────────────────────────────────────────────────── */
export function parseCardText(text: string): ReactNode {
  if (!text) return null;
  const lines = text.replace(/\r/g, '').split('\n');
  const nodes: ReactNode[] = [];
  let key = 0;

  for (const raw of lines) {
    const line = raw.trim();

    if (!line) {
      nodes.push(<div key={key++} className="card-text-gap" />);
      continue;
    }

    const isBullet = line.startsWith('- ') || line.startsWith('• ');
    const content = parseInline(isBullet ? line.slice(2) : line);

    if (isBullet) {
      nodes.push(
        <div key={key++} className="card-text-bullet">
          <ChevronRight size={12} className="card-text-bullet__arrow" />
          <span>{content}</span>
        </div>
      );
    } else {
      nodes.push(<p key={key++} className="card-text-para">{content}</p>);
    }
  }

  return <>{nodes}</>;
}
